import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getSalesByMitra, updateSale, deleteSale, getAllBooks, getCustomersByMitra, uploadBuktiPembayaran } from '../../supabase/database';
import { FaClipboardList, FaPlus, FaEdit, FaTrash, FaTimes, FaUpload, FaEye } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import Spinner from '../../components/ui/Spinner';
import Modal from '../../components/ui/Modal';
import { getCategoryDiscountQuota } from '../../utils/categoryQuota';

const StatusBadge = ({ status }) => {
  const statusMap = {
    'menunggu validasi': 'bg-yellow-100 text-yellow-800',
    'ditolak': 'bg-red-100 text-red-800',
    'diproses': 'bg-blue-100 text-blue-800',
    'dikirim sebagian': 'bg-purple-100 text-purple-800',
    'dikirim seluruhnya': 'bg-emerald-100 text-emerald-800',
  };

  const statusText = {
    'menunggu validasi': 'Menunggu Proses',
    'ditolak': 'Ditolak',
    'diproses': 'Diproses',
    'dikirim sebagian': 'Dikirim Sebagian',
    'dikirim seluruhnya': 'Dikirim Seluruhnya',
  };

  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${statusMap[status] || 'bg-gray-100 text-gray-800'}`}>
      {statusText[status] || status}
    </span>
  );
};

const PaymentBadge = ({ payment }) => {
  const paymentMap = {
    'belum dibayar': 'bg-gray-100 text-gray-800',
    'uang muka': 'bg-orange-100 text-orange-800',
    'lunas sebagian': 'bg-blue-100 text-blue-800',
    'lunas': 'bg-green-100 text-green-800',
  };

  const paymentText = {
    'belum dibayar': 'Belum Dibayar',
    'uang muka': 'Uang Muka',
    'lunas sebagian': 'Lunas Sebagian',
    'lunas': 'Lunas',
  };

  return (
    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${paymentMap[payment] || 'bg-gray-100 text-gray-800'}`}>
      {paymentText[payment] || payment}
    </span>
  );
};

const RiwayatPesanan = () => {
  const { currentUser } = useAuth();
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [filteredSales, setFilteredSales] = useState([]);
  
  // Edit modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [editForm, setEditForm] = useState({
    pelanggan: {
      nama_pelanggan: '',
      no_wa: '',
      alamat: ''
    },
    notes: '',
    items: []
  });
  const [saving, setSaving] = useState(false);

  // Book search states
  const [allBooks, setAllBooks] = useState([]);
  const [searchInput, setSearchInput] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [selectedBook, setSelectedBook] = useState(null);
  const [quantity, setQuantity] = useState(1);
  
  // Customer search states
  const [customers, setCustomers] = useState([]);
  const [customerSearchInput, setCustomerSearchInput] = useState('');
  const [customerSuggestions, setCustomerSuggestions] = useState([]);
  // const [selectedCustomerForEdit, setSelectedCustomerForEdit] = useState(null); // Disabled
  const [showCustomerSelector, setShowCustomerSelector] = useState(false);

  // Upload bukti pembayaran states
  const [showBuktiModal, setShowBuktiModal] = useState(false);
  const [uploadingSale, setUploadingSale] = useState(null);
  const [buktiFile, setBuktiFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  // Delete confirm states
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  
  // Image modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState('');

  useEffect(() => {
    if (currentUser) {
      console.log('🔍 RiwayatPesanan - currentUser:', {
        uid: currentUser.uid,
        id: currentUser.id,
        email: currentUser.email
      });
      
      // Panggil getSalesByMitra tanpa filter status untuk mendapatkan semua pesanan
      getSalesByMitra(currentUser.uid) 
        .then(data => {
          console.log('📊 RiwayatPesanan - received sales data:', data);
          setSales(data);
          setLoading(false);
        })
        .catch(err => {
          console.error('❌ RiwayatPesanan - getSalesByMitra error:', err);
          setLoading(false);
        });
      
      // Load all books for search
      getAllBooks()
        .then(books => {
          setAllBooks(books);
        })
        .catch(err => {
          console.error('Error loading books:', err);
        });
      
      // Load customers for this mitra
      getCustomersByMitra(currentUser.uid)
        .then(customerList => {
          console.log('📋 RiwayatPesanan - loaded customers:', customerList);
          setCustomers(customerList);
        })
        .catch(err => {
          console.error('Error loading customers:', err);
        });
    }
  }, [currentUser]);

  // Filter sales based on status and payment filters
  useEffect(() => {
    let filtered = sales;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(sale => sale.status === statusFilter);
    }
    
    if (paymentFilter !== 'all') {
      filtered = filtered.filter(sale => (sale.pembayaran || 'belum dibayar') === paymentFilter);
    }
    
    setFilteredSales(filtered);
  }, [sales, statusFilter, paymentFilter]);

  const handleEdit = (saleId) => {
    const sale = sales.find(s => s.id === saleId);
    if (sale) {
      setEditingSale(sale);
      setEditForm({
        pelanggan: {
          nama_pelanggan: sale.pelanggan?.nama_pelanggan || sale.namaSekolah || '',
          no_wa: sale.pelanggan?.no_wa || '',
          alamat: sale.pelanggan?.alamat || ''
        },
        notes: sale.notes || '',
        items: sale.items || []
      });
      setShowEditModal(true);
    }
  };

  const handleSaveEdit = async () => {
    if (!editingSale) return;

    try {
      setSaving(true);
      
      // Validate and apply category discount quotas for each item
      const validatedItems = editForm.items.map(item => {
        const categoryQuota = getCategoryDiscountQuota(item.kategori);
        const maxDiscount = Math.min(item.diskonPerItem || 0, categoryQuota);
        
        return {
          ...item,
          diskonPerItem: maxDiscount,
          diskon_per_item: maxDiscount // For database compatibility
        };
      });
      
      // Prepare updated data
      const updatedSaleData = {
        pelanggan: editForm.pelanggan,
        namaSekolah: editForm.pelanggan.nama_pelanggan, // For backward compatibility
        notes: editForm.notes,
        items: validatedItems,
        // Recalculate totals with validated discounts
        totalHarga: validatedItems.reduce((sum, item) => sum + (item.harga_buku * item.kuantitas), 0),
        hargaSetelahDiskon: validatedItems.reduce((sum, item) => {
          const hargaSetelahDiskon = item.harga_buku - (item.harga_buku * (item.diskonPerItem || 0) / 100);
          return sum + (hargaSetelahDiskon * item.kuantitas);
        }, 0)
      };
      
      // Calculate subtotal (after discount + shipping)
      updatedSaleData.subtotal = updatedSaleData.hargaSetelahDiskon + (editingSale.ongkir || 0);

      await updateSale(editingSale.id, updatedSaleData);
      
      // Refresh sales list
      const updatedSales = await getSalesByMitra(currentUser.uid);
      setSales(updatedSales);
      
      setShowEditModal(false);
      setEditingSale(null);
      alert('Pesanan berhasil diperbarui!');
    } catch (error) {
      console.error('Error updating sale:', error);
      alert('Gagal memperbarui pesanan: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEditPelangganChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      pelanggan: {
        ...prev.pelanggan,
        [field]: value
      }
    }));
  };

  const handleEditItemChange = (index, field, value) => {
    setEditForm(prev => ({
      ...prev,
      items: prev.items.map((item, i) => 
        i === index ? { ...item, [field]: value } : item
      )
    }));
  };

  const handleRemoveItem = (index) => {
    if (editForm.items.length > 1) {
      setEditForm(prev => ({
        ...prev,
        items: prev.items.filter((_, i) => i !== index)
      }));
    } else {
      alert('Pesanan harus memiliki minimal 1 item');
    }
  };

  // Book search functions
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setSelectedBook(null);

    if (value.length > 1) {
      const filteredBooks = allBooks.filter(book =>
        String(book.kode_buku).toLowerCase().includes(value.toLowerCase()) ||
        String(book.judul_buku).toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setSuggestions(filteredBooks);
    } else {
      setSuggestions([]);
    }
  };

  const handleSuggestionClick = (book) => {
    setSearchInput(book.kode_buku);
    setSelectedBook(book);
    setSuggestions([]);
  };

  // Customer search functions
  const handleCustomerSearchChange = (e) => {
    const value = e.target.value;
    setCustomerSearchInput(value);

    if (value.length > 1) {
      const filteredCustomers = customers.filter(customer => 
        customer.nama?.toLowerCase().includes(value.toLowerCase()) ||
        customer.no_wa?.includes(value) ||
        customer.alamat?.toLowerCase().includes(value.toLowerCase())
      ).slice(0, 5);
      setCustomerSuggestions(filteredCustomers);
    } else {
      setCustomerSuggestions([]);
    }
  };

  const handleCustomerSuggestionClick = (customer) => {
    setCustomerSearchInput('');
    setCustomerSuggestions([]);
    // Auto-fill customer data
    handleEditPelangganChange('nama_pelanggan', customer.nama);
    handleEditPelangganChange('no_wa', customer.no_wa);
    handleEditPelangganChange('alamat', customer.alamat);
    setShowCustomerSelector(false);
  };

  const handleAddBookToOrder = () => {
    if (!selectedBook) {
      alert("Pilih buku dari daftar saran terlebih dahulu.");
      return;
    }

    const existingItemIndex = editForm.items.findIndex(item => item.kode_buku === selectedBook.kode_buku);

    if (existingItemIndex > -1) {
      // Update existing item quantity
      const updatedItems = editForm.items.map((item, index) => 
        index === existingItemIndex 
          ? { ...item, kuantitas: item.kuantitas + quantity }
          : item
      );
      setEditForm(prev => ({ ...prev, items: updatedItems }));
    } else {
      // Add new item dengan kuota diskon otomatis
      const categoryQuota = getCategoryDiscountQuota(selectedBook.kategori);
      setEditForm(prev => ({
        ...prev,
        items: [...prev.items, { ...selectedBook, kuantitas: quantity, diskonPerItem: categoryQuota }]
      }));
    }

    // Reset form
    setSelectedBook(null);
    setSearchInput('');
    setQuantity(1);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingSale(null);
    setEditForm({
      pelanggan: {
        nama_pelanggan: '',
        no_wa: '',
        alamat: ''
      },
      items: [],
      notes: ''
    });
    // Reset search form
    setSearchInput('');
    setSuggestions([]);
    setSelectedBook(null);
    setQuantity(1);
    // Reset customer search
    setCustomerSearchInput('');
    setCustomerSuggestions([]);
    setShowCustomerSelector(false);
  };

  // Upload bukti pembayaran functions
  const handleBuktiUploadClick = (sale) => {
    setUploadingSale(sale);
    setShowBuktiModal(true);
  };

  const closeBuktiModal = () => {
    setShowBuktiModal(false);
    setUploadingSale(null);
    setBuktiFile(null);
  };

  const handleBuktiFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validasi ukuran file (max 1MB)
      if (file.size > 1024 * 1024) {
        alert('Ukuran file maksimal 1MB');
        return;
      }
      
      // Validasi tipe file
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedTypes.includes(file.type)) {
        alert('Tipe file harus JPG, JPEG, atau PNG');
        return;
      }
      
      setBuktiFile(file);
    }
  };

  const handleUploadBukti = async () => {
    if (!buktiFile || !uploadingSale) {
      alert('Pilih file bukti pembayaran terlebih dahulu');
      return;
    }

    setUploading(true);
    try {
      await uploadBuktiPembayaran(uploadingSale.id, buktiFile, uploadingSale.no_pesanan);
      
      // Refresh sales data
      const updatedSales = await getSalesByMitra(currentUser.uid);
      setSales(updatedSales);
      
      alert('Bukti pembayaran berhasil diupload!');
      closeBuktiModal();
    } catch (error) {
      console.error('Error uploading bukti pembayaran:', error);
      alert('Gagal upload bukti pembayaran: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  // Upload functions - DISABLED: Sekarang hanya admin yang bisa input resi
  // const handleUploadClick = (sale) => {
  //   setUploadingSale(sale);
  //   setShowUploadModal(true);
  // };

  // const closeUploadModal = () => {
  //   setShowUploadModal(false);
  //   setUploadingSale(null);
  //   setResiNumber('');
  //   setBuktiPembayaranFile(null);
  // };

  // const handleFileSelect = (file) => {
  //   // Validasi ukuran file (max 1MB)
  //   if (file.size > 1024 * 1024) {
  //     alert('Ukuran file maksimal 1MB');
  //     return;
  //   }

  //   // Validasi tipe file
  //   const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
  //   if (!allowedTypes.includes(file.type)) {
  //     alert('Tipe file harus JPG, JPEG, atau PNG');
  //     return;
  //   }

  //   setBuktiPembayaranFile(file);
  // };

  // const handleUploadFiles = async () => {
  //   if (!resiNumber.trim() && !buktiPembayaranFile) {
  //     alert('Masukkan nomor resi atau pilih file bukti pembayaran');
  //     return;
  //   }

  //   setUploading(true);
  //   try {
  //     const uploadData = {};

  //     // Simpan nomor resi sebagai text jika ada
  //     if (resiNumber.trim()) {
  //       uploadData.resi_number = resiNumber.trim();
  //     }

  //     // Upload bukti pembayaran jika ada
  //     if (buktiPembayaranFile) {
  //       const buktiURL = await uploadFile(buktiPembayaranFile, 'bukti-pembayaran', uploadingSale.noPesanan);
  //       uploadData.bukti_pembayaran_url = buktiURL;
  //     }

  //     // Update sale dengan data upload
  //     await updateSaleWithFiles(uploadingSale.id, uploadData);

  //     // Refresh data
  //     const updatedSales = await getSalesByMitra(currentUser.uid);
  //     setSales(updatedSales);

  //     alert('File berhasil diupload!');
  //     closeUploadModal();
  //   } catch (error) {
  //     console.error('Error uploading files:', error);
  //     alert('Gagal upload file: ' + error.message);
  //   } finally {
  //     setUploading(false);
  //   }
  // };

  const openDeleteModal = (sale) => {
    setSaleToDelete(sale);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setSaleToDelete(null);
  };

  const confirmDelete = async () => {
    if (!saleToDelete) return;
    try {
      setLoading(true);
      console.log('Deleting sale:', saleToDelete.id, 'for user:', currentUser.uid);
      await deleteSale(saleToDelete.id);
      // Refresh the sales list after deletion
      const updatedSales = await getSalesByMitra(currentUser.uid);
      setSales(updatedSales);
      alert('Pesanan berhasil dihapus!');
    } catch (error) {
      console.error('Error deleting sale:', error);
      // Show more detailed error message
      let errorMessage = error.message || 'Terjadi kesalahan saat menghapus pesanan';
      if (error.message.includes('permission') || error.message.includes('izin')) {
        errorMessage = 'Anda tidak memiliki izin untuk menghapus pesanan ini. Pastikan Anda adalah pemilik pesanan.';
      } else if (error.message.includes('not found') || error.message.includes('tidak ditemukan')) {
        errorMessage = 'Pesanan tidak ditemukan atau sudah dihapus.';
      }
      alert('Gagal menghapus pesanan: ' + errorMessage);
    } finally {
      setLoading(false);
      closeDeleteModal();
    }
  };

  const handleViewDetails = (sale) => {
    setSelectedSale(sale);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedSale(null);
  };
  
  const handleImageClick = (imageUrl) => {
    setSelectedImageUrl(imageUrl);
    setShowImageModal(true);
  };
  
  const closeImageModal = () => {
    setShowImageModal(false);
    setSelectedImageUrl('');
  };

  if (loading) return <Spinner />;

  return (
    <div className="p-4 md:p-8 bg-white min-h-full">
      <div className="flex items-center gap-4 mb-4">
        <FaClipboardList className="text-4xl text-amber-500" />
        <h1 className="text-3xl font-bold text-gray-800">Daftar Pesanan</h1>
      </div>
      
      <div className="mb-6 flex flex-col gap-4">
        <Link
          to="/mitra/input-penjualan"
          className="flex items-center gap-2 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg font-semibold transition-colors duration-200 shadow-md hover:shadow-lg w-fit"
        >
          <FaPlus className="text-sm" />
          Tambah Pesanan
        </Link>
        
        {/* Info tentang resi */}
        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r-lg">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Informasi:</strong> Input nomor resi dan bukti pembayaran sekarang dikelola oleh admin. 
                Anda dapat melihat status resi yang sudah diinput admin di tabel di bawah ini.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-6 bg-gray-50 p-4 rounded-lg">
        <h3 className="text-sm font-medium text-gray-700 mb-3">Filter Pesanan</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">Semua Status</option>
              <option value="menunggu validasi">Menunggu Proses</option>
              <option value="diproses">Diproses</option>
              <option value="dikirim sebagian">Dikirim Sebagian</option>
              <option value="dikirim seluruhnya">Dikirim Seluruhnya</option>
              <option value="ditolak">Ditolak</option>
            </select>
          </div>
          
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">Pembayaran</label>
            <select
              value={paymentFilter}
              onChange={(e) => setPaymentFilter(e.target.value)}
              className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            >
              <option value="all">Semua Pembayaran</option>
              <option value="belum dibayar">Belum Dibayar</option>
              <option value="uang muka">Uang Muka</option>
              <option value="lunas sebagian">Lunas Sebagian</option>
              <option value="lunas">Lunas</option>
            </select>
          </div>

          <div className="md:col-span-2 lg:col-span-2 flex items-end">
            <button
              onClick={() => {
                setStatusFilter('all');
                setPaymentFilter('all');
              }}
              className="px-4 py-2 bg-gray-500 hover:bg-gray-600 text-white text-sm rounded-lg transition-colors duration-200"
            >
              Reset Filter
            </button>
            <div className="ml-4 text-sm text-gray-600 flex items-center">
              Menampilkan {filteredSales.length} dari {sales.length} pesanan
            </div>
          </div>
        </div>
      </div>

      {/* Notification for missing customer data */}
      {filteredSales.some(sale => !sale.pelanggan?.nama_pelanggan && !sale.namaSekolah) && (
        <div className="mb-6 bg-blue-50 border-l-4 border-blue-400 p-4 rounded-r-lg">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                <strong>Tips:</strong> Untuk pesanan yang akan datang, pastikan memilih pelanggan dari Daftar Pelanggan saat input pesanan.
              </p>
              <p className="text-xs text-blue-600 mt-1">
                Kelola pelanggan di menu <strong>Daftar Pelanggan</strong> untuk memudahkan input pesanan selanjutnya.
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">No Pesanan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Tanggal</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pelanggan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subtotal</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Catatan</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pembayaran</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Resi</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bukti Bayar</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aksi</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredSales.map(sale => (
              <tr key={sale.id}>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  <div className="space-y-1">
                    <div>{sale.noPesanan || '-'}</div>
                    <button
                      onClick={() => handleViewDetails(sale)}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 hover:bg-blue-200 rounded transition-colors duration-200"
                      title="Lihat detail pesanan"
                    >
                      Lihat Detail Pesanan
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {(() => {
                    try {
                      const dateValue = sale.createdAt || sale.created_at;
                      if (!dateValue) return 'N/A';
                      
                      const date = new Date(dateValue);
                      if (isNaN(date.getTime())) return 'N/A';
                      
                      return date.toLocaleDateString('id-ID', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit'
                      });
                    } catch (error) {
                      console.error('Error formatting date:', error);
                      return 'N/A';
                    }
                  })()}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {sale.pelanggan?.nama_pelanggan || sale.namaSekolah || (
                    <span className="text-gray-400 text-xs">Belum diisi</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  Rp {sale.subtotal.toLocaleString('id-ID')}
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs">
                  <div className="truncate" title={sale.notes || '-'}>
                    {sale.notes || '-'}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <StatusBadge status={sale.status} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <PaymentBadge payment={sale.pembayaran || 'belum dibayar'} />
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {sale.resi_number ? (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      📋 {sale.resi_number}
                    </span>
                  ) : (
                    <span className="text-gray-400 text-xs">Belum ada</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex flex-col gap-1">
                    {sale.bukti_pembayaran_url ? (
                      <button
                        onClick={() => handleImageClick(sale.bukti_pembayaran_url)}
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors cursor-pointer"
                        title="Lihat bukti pembayaran"
                      >
                        <FaEye className="mr-1" /> Lihat Bukti
                      </button>
                    ) : (
                      <span className="text-gray-400 text-xs">Belum ada</span>
                    )}
                    <button
                      onClick={() => handleBuktiUploadClick(sale)}
                      className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 hover:bg-blue-200 transition-colors cursor-pointer"
                      title={sale.bukti_pembayaran_url ? "Ganti bukti pembayaran" : "Upload bukti pembayaran"}
                    >
                      <FaUpload className="mr-1" /> {sale.bukti_pembayaran_url ? "Ganti" : "Upload"}
                    </button>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(sale.id)}
                      className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-all duration-200"
                      title="Edit pesanan"
                    >
                      <FaEdit className="text-sm" />
                    </button>
                    <button
                      onClick={() => openDeleteModal(sale)}
                      className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-all duration-200"
                      title="Hapus pesanan"
                    >
                      <FaTrash className="text-sm" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {!loading && filteredSales.length === 0 && sales.length === 0 && <p className="text-center p-4 text-gray-500">Anda belum memiliki pesanan.</p>}
        {!loading && filteredSales.length === 0 && sales.length > 0 && <p className="text-center p-4 text-gray-500">Tidak ada pesanan yang sesuai dengan filter.</p>}
      </div>

      {/* Modal Detail Pesanan */}
      {showModal && selectedSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">Detail Pesanan</h2>
              <button
                onClick={closeModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Order Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">Informasi Pesanan</h3>
                  <div className="space-y-2 text-sm">
                    <div><span className="font-medium">No Pesanan:</span> {selectedSale.noPesanan || '-'}</div>
                    <div><span className="font-medium">Tanggal:</span> {selectedSale.createdAt?.toLocaleDateString ? selectedSale.createdAt.toLocaleDateString('id-ID') : selectedSale.createdAt ? new Date(selectedSale.createdAt).toLocaleDateString('id-ID') : 'N/A'}</div>
                    <div><span className="font-medium">Status:</span> <StatusBadge status={selectedSale.status} /></div>
                    <div><span className="font-medium">Kode Mitra:</span> {selectedSale.kode_mitra}</div>
                    <div><span className="font-medium">Nama Mitra:</span> {selectedSale.nama_mitra}</div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">Informasi Pelanggan</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="font-medium">Nama:</span> 
                      {selectedSale.pelanggan?.nama_pelanggan || selectedSale.namaSekolah || (
                        <span className="text-gray-400 italic ml-1">Belum diisi</span>
                      )}
                    </div>
                    <div>
                      <span className="font-medium">No. WhatsApp:</span> 
                      {selectedSale.pelanggan?.no_wa || (
                        <span className="text-gray-400 italic ml-1">Belum diisi</span>
                      )}
                    </div>
                    <div>
                      <span className="font-medium">Alamat:</span> 
                      {selectedSale.pelanggan?.alamat || (
                        <span className="text-gray-400 italic ml-1">Belum diisi</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Item Pesanan</h3>
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produk</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Harga</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Diskon</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {selectedSale.items?.map((item, index) => {
                        const hargaSetelahDiskon = item.harga_buku - (item.harga_buku * (item.diskonPerItem || 0) / 100);
                        const totalItem = hargaSetelahDiskon * item.kuantitas;
                        return (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.judul_buku}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right">Rp {item.harga_buku.toLocaleString('id-ID')}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-center">{item.kuantitas}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-center">{item.diskonPerItem || 0}%</td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">Rp {totalItem.toLocaleString('id-ID')}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  {selectedSale.notes && (
                    <div className="bg-yellow-50 p-4 rounded-lg">
                      <h3 className="font-semibold text-gray-800 mb-2">Catatan</h3>
                      <p className="text-sm text-gray-700">{selectedSale.notes}</p>
                    </div>
                  )}
                  {/* Resi & Bukti Pembayaran */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h3 className="font-semibold text-gray-800 mb-2">Pengiriman & Bukti Pembayaran</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">Nomor Resi:</span>
                        <span className="font-medium text-gray-900">{selectedSale.resi_number || '-'}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-gray-700">Bukti Pembayaran:</span>
                        {selectedSale.bukti_pembayaran_url ? (
                          <a
                            href={selectedSale.bukti_pembayaran_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 font-medium"
                          >
                            Lihat
                          </a>
                        ) : (
                          <span className="text-gray-500">-</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-amber-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">Ringkasan Pembayaran</h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>Status Pembayaran:</span>
                      <span><PaymentBadge payment={selectedSale.pembayaran || 'belum dibayar'} /></span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Harga:</span>
                      <span>Rp {(selectedSale.total_harga || selectedSale.totalHarga || 0).toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-red-600">Diskon:</span>
                      <span className="text-red-600">- Rp {(((selectedSale.total_harga || selectedSale.totalHarga || 0) - (selectedSale.harga_setelah_diskon || selectedSale.hargaSetelahDiskon || 0))).toLocaleString('id-ID')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Ongkos Kirim:</span>
                      <span>Rp {(selectedSale.ongkir || 0).toLocaleString('id-ID')}</span>
                    </div>
                    <hr className="my-2" />
                    <div className="flex justify-between font-bold text-lg">
                      <span>Total:</span>
                      <span>Rp {(selectedSale.subtotal || 0).toLocaleString('id-ID')}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end p-6 border-t border-gray-200">
              <button
                onClick={closeModal}
                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
              >
                Tutup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Edit Pesanan */}
      {showEditModal && editingSale && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-800">Edit Pesanan</h2>
              <button
                onClick={closeEditModal}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
              >
                <FaTimes className="text-lg" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              {/* Order Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="font-semibold text-gray-800 mb-3">Informasi Pesanan</h3>
                  <div className="space-y-3 text-sm">
                    <div><span className="font-medium">No Pesanan:</span> {editingSale.noPesanan || '-'}</div>
                    <div><span className="font-medium">Tanggal:</span> {editingSale.createdAt?.toLocaleDateString ? editingSale.createdAt.toLocaleDateString('id-ID') : editingSale.createdAt ? new Date(editingSale.createdAt).toLocaleDateString('id-ID') : 'N/A'}</div>
                    <div><span className="font-medium">Status:</span> <StatusBadge status={editingSale.status} /></div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Catatan Pesanan</label>
                      <textarea
                        value={editForm.notes}
                        onChange={(e) => handleEditFormChange('notes', e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        rows="3"
                        placeholder="Catatan untuk pesanan ini..."
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-800">Informasi Pelanggan</h3>
                    {customers.length > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowCustomerSelector(!showCustomerSelector)}
                        className="px-3 py-1 bg-green-100 text-green-700 hover:bg-green-200 text-xs rounded transition-colors"
                      >
                        {showCustomerSelector ? 'Tutup' : 'Pilih dari List'}
                      </button>
                    )}
                  </div>
                  
                  {/* Customer Selector */}
                  {showCustomerSelector && customers.length > 0 && (
                    <div className="mb-4 p-3 bg-white rounded border">
                      <label className="block text-xs font-medium text-gray-700 mb-2">Cari Pelanggan Existing</label>
                      <div className="relative">
                        <input
                          type="text"
                          value={customerSearchInput}
                          onChange={handleCustomerSearchChange}
                          className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
                          placeholder="Ketik nama, nomor WA, atau alamat pelanggan..."
                        />
                        {customerSuggestions.length > 0 && (
                          <div className="absolute z-10 w-full bg-white border border-gray-300 rounded-b max-h-48 overflow-y-auto">
                            {customerSuggestions.map((customer) => (
                              <div
                                key={customer.id}
                                onClick={() => handleCustomerSuggestionClick(customer)}
                                className="p-2 hover:bg-gray-100 cursor-pointer border-b text-sm"
                              >
                                <div className="font-medium">{customer.nama}</div>
                                <div className="text-xs text-gray-600">{customer.no_wa} - {customer.alamat}</div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="mt-2 text-xs text-gray-600">
                        Pilih dari {customers.length} pelanggan yang sudah ada
                      </div>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Nama Pelanggan</label>
                      <input
                        type="text"
                        value={editForm.pelanggan.nama_pelanggan}
                        onChange={(e) => handleEditPelangganChange('nama_pelanggan', e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="Nama pelanggan"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">No. WhatsApp</label>
                      <input
                        type="text"
                        value={editForm.pelanggan.no_wa}
                        onChange={(e) => handleEditPelangganChange('no_wa', e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        placeholder="Nomor WhatsApp"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Alamat</label>
                      <textarea
                        value={editForm.pelanggan.alamat}
                        onChange={(e) => handleEditPelangganChange('alamat', e.target.value)}
                        className="w-full text-sm border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
                        rows="3"
                        placeholder="Alamat lengkap"
                      />
                    </div>
                  </div>
                </div>
              </div>


              {/* Order Items */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">Item Pesanan</h3>
                
                {/* Add New Book Section */}
                <div className="bg-blue-50 p-4 rounded-lg mb-4">
                  <h4 className="font-medium text-blue-800 mb-3">Tambah Buku Baru</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div className="relative">
                      <label className="block text-xs font-medium text-gray-700 mb-1">Cari Kode / Judul Buku</label>
                      <input
                        type="text"
                        value={searchInput}
                        onChange={handleSearchChange}
                        className="w-full text-sm p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="Ketik untuk mencari..."
                      />
                      {suggestions.length > 0 && (
                        <ul className="absolute z-20 w-full bg-white border rounded-md mt-1 shadow-lg max-h-60 overflow-y-auto">
                          {suggestions.map(book => (
                            <li
                              key={book.kode_buku}
                              onClick={() => handleSuggestionClick(book)}
                              className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
                            >
                              <span className="font-bold text-blue-600">{book.kode_buku}</span> - {book.judul_buku}
                              <div className="text-xs text-gray-500">Rp {book.harga_buku.toLocaleString('id-ID')}</div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Kuantitas</label>
                      <input
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(Math.max(1, Number(e.target.value)))}
                        className="w-full text-sm p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="1"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={handleAddBookToOrder}
                        disabled={!selectedBook}
                        className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white text-sm rounded transition-colors duration-200 flex items-center justify-center gap-2"
                      >
                        <FaPlus className="text-xs" />
                        Tambah
                      </button>
                    </div>
                  </div>
                  {selectedBook && (
                    <div className="mt-3 p-3 bg-blue-100 rounded border border-blue-200">
                      <div className="text-sm">
                        <span className="font-semibold text-blue-800">{selectedBook.judul_buku}</span>
                        <div className="text-xs text-blue-600">Kode: {selectedBook.kode_buku} | Harga: Rp {selectedBook.harga_buku.toLocaleString('id-ID')}</div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200 border border-gray-200 rounded-lg">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Produk</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Harga</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Qty</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Diskon (%)</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                        <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Aksi</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {editForm.items.map((item, index) => {
                        const hargaSetelahDiskon = item.harga_buku - (item.harga_buku * (item.diskonPerItem || 0) / 100);
                        const totalItem = hargaSetelahDiskon * item.kuantitas;
                        return (
                          <tr key={index}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.judul_buku}</td>
                            <td className="px-4 py-3 text-sm text-gray-700 text-right">Rp {item.harga_buku.toLocaleString('id-ID')}</td>
                            <td className="px-4 py-3 text-center">
                              <input
                                type="number"
                                value={item.kuantitas}
                                onChange={(e) => handleEditItemChange(index, 'kuantitas', parseInt(e.target.value) || 0)}
                                className="w-16 text-sm border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-amber-500"
                                min="1"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <div className="space-y-1">
                                <input
                                  type="number"
                                  value={item.diskonPerItem || 0}
                                  onChange={(e) => {
                                    const inputValue = parseFloat(e.target.value) || 0;
                                    const categoryQuota = getCategoryDiscountQuota(item.kategori);
                                    const validValue = Math.min(inputValue, categoryQuota);
                                    handleEditItemChange(index, 'diskonPerItem', validValue);
                                  }}
                                  className="w-16 text-sm border border-gray-300 rounded px-2 py-1 text-center focus:outline-none focus:ring-2 focus:ring-amber-500"
                                  min="0"
                                  max={getCategoryDiscountQuota(item.kategori) || 100}
                                  step="0.1"
                                  title={`Maksimal diskon: ${getCategoryDiscountQuota(item.kategori)}%`}
                                />
                                <div className="text-xs text-gray-500">
                                  Max: {getCategoryDiscountQuota(item.kategori)}%
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right">Rp {totalItem.toLocaleString('id-ID')}</td>
                            <td className="px-4 py-3 text-center">
                              <button
                                onClick={() => handleRemoveItem(index)}
                                className="p-1 text-red-600 hover:text-red-800 hover:bg-red-50 rounded transition-all duration-200"
                                title="Hapus item"
                              >
                                <FaTrash className="text-xs" />
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-semibold text-gray-800 mb-3">Ringkasan Total</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span>Total Items:</span>
                    <span>Rp {editForm.items.reduce((sum, item) => sum + (item.harga_buku * item.kuantitas), 0).toLocaleString('id-ID')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Setelah Diskon:</span>
                    <span>Rp {editForm.items.reduce((sum, item) => {
                      const hargaSetelahDiskon = item.harga_buku - (item.harga_buku * (item.diskonPerItem || 0) / 100);
                      return sum + (hargaSetelahDiskon * item.kuantitas);
                    }, 0).toLocaleString('id-ID')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Ongkos Kirim:</span>
                    <span>Rp {(editingSale.ongkir || 0).toLocaleString('id-ID')}</span>
                  </div>
                  <hr className="my-2" />
                  <div className="flex justify-between font-bold text-lg">
                    <span>Total Akhir:</span>
                    <span>Rp {(editForm.items.reduce((sum, item) => {
                      const hargaSetelahDiskon = item.harga_buku - (item.harga_buku * (item.diskonPerItem || 0) / 100);
                      return sum + (hargaSetelahDiskon * item.kuantitas);
                    }, 0) + (editingSale.ongkir || 0)).toLocaleString('id-ID')}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex justify-end gap-3 p-6 border-t border-gray-200">
              <button
                onClick={closeEditModal}
                className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg transition-colors duration-200"
                disabled={saving}
              >
                Batal
              </button>
              <button
                onClick={handleSaveEdit}
                className="px-6 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg transition-colors duration-200 flex items-center gap-2"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Menyimpan...
                  </>
                ) : (
                  'Simpan Perubahan'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Konfirmasi Hapus */}
      <Modal
        isOpen={showDeleteModal}
        onClose={closeDeleteModal}
        onConfirm={confirmDelete}
        title="Hapus Pesanan"
        type="confirm"
      >
        Apakah Anda yakin ingin menghapus pesanan ini? Tindakan ini tidak dapat dibatalkan.
      </Modal>

      {/* Upload Bukti Pembayaran Modal */}
      {showBuktiModal && (
        <Modal
          isOpen={showBuktiModal}
          onClose={closeBuktiModal}
          title=""
          type="form"
        >
          <div className="p-8 space-y-8">
            {/* Minimalist Header - Clean Typography */}
            <div className="text-center space-y-3">
              <h2 className="text-2xl font-light text-gray-900 tracking-tight">
                Upload Bukti Pembayaran
              </h2>
              <p className="text-sm text-gray-500 font-light">
                Pesanan <span className="font-medium text-gray-900">{uploadingSale?.no_pesanan}</span>
              </p>
            </div>

            {/* Large Drag & Drop Area - Minimalist Design */}
            <div className="space-y-6">
              <label className="block">
                <div className="relative mt-2 flex justify-center px-12 py-16 border border-gray-200 rounded-2xl hover:border-cyan-400 transition-all duration-300 cursor-pointer group bg-white">
                  <div className="text-center space-y-4">
                    {/* Thin Line Icon - Upload */}
                    <svg
                      className="mx-auto h-16 w-16 text-gray-300 group-hover:text-cyan-400 transition-colors duration-300"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      strokeWidth="1"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                      />
                    </svg>
                    
                    <div className="space-y-2">
                      <label
                        htmlFor="bukti-upload"
                        className="relative cursor-pointer"
                      >
                        <span className="text-base font-light text-gray-600">
                          <span className="text-cyan-500 font-normal hover:text-cyan-600 transition-colors">Pilih file</span> atau seret file ke sini
                        </span>
                        <input
                          id="bukti-upload"
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={handleBuktiFileChange}
                          className="sr-only"
                        />
                      </label>
                      <p className="text-xs text-gray-400 font-light tracking-wide">
                        JPG, PNG • Maks 1MB
                      </p>
                    </div>
                  </div>
                </div>
              </label>

              {/* Minimal File Preview */}
              {buktiFile && (
                <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="flex-shrink-0">
                      <svg className="h-10 w-10 text-cyan-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-normal text-gray-900 truncate">
                        {buktiFile.name}
                      </p>
                      <p className="text-xs text-gray-400 font-light mt-0.5">
                        {(buktiFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Minimalist Action Buttons */}
            <div className="flex justify-center gap-4 pt-6">
              <button
                type="button"
                onClick={closeBuktiModal}
                disabled={uploading}
                className="px-8 py-3 text-sm font-normal text-gray-600 bg-white hover:bg-gray-50 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 disabled:opacity-50 transition-all duration-200"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleUploadBukti}
                disabled={uploading || !buktiFile}
                className="px-8 py-3 text-sm font-normal text-white bg-cyan-500 hover:bg-cyan-600 rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2 transition-all duration-200 shadow-sm hover:shadow-md"
              >
                {uploading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border border-white border-t-transparent"></div>
                    <span>Mengupload...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10" />
                    </svg>
                    <span>Upload Bukti</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* Image Modal */}
      {showImageModal && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          onClick={closeImageModal}
        >
          <div 
            className="relative bg-white rounded-lg max-w-4xl max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">Bukti Pembayaran</h3>
              <button
                onClick={closeImageModal}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FaTimes className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 text-center">
              <img
                src={selectedImageUrl}
                alt="Bukti Pembayaran"
                className="max-w-full max-h-[70vh] object-contain mx-auto"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgdmlld0JveD0iMCAwIDIwMCAyMDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik05NCA4NEM5NCA3OS41ODE3IDk3LjU4MTcgNzYgMTAyIDc2QzEwNi40MTggNzYgMTEwIDc5LjU4MTcgMTEwIDg0VjEwMEMxMTAgMTA0LjQxOCAxMDYuNDE4IDEwOCAxMDIgMTA4Qzk3LjU4MTcgMTA4IDk0IDEwNC40MTggOTQgMTAwVjg0WiIgZmlsbD0iIzlDQTNBRiIvPgo8cGF0aCBkPSJNMTAyIDEyNEM5Ny41ODE3IDEyNCA5NCAxMjAuNDE4IDk0IDExNkM5NCAxMTEuNTgyIDk3LjU4MTcgMTA4IDEwMiAxMDhDMTA2LjQxOCAxMDggMTEwIDExMS41ODIgMTEwIDExNkMxMTAgMTIwLjQxOCAxMDYuNDE4IDEyNCAxMDIgMTI0WiIgZmlsbD0iIzlDQTNBRiIvPgo8L3N2Zz4='; // Fallback image
                  e.target.alt = 'Gambar tidak dapat dimuat';
                }}
              />
              <p className="text-sm text-gray-500 mt-2">Klik di luar gambar untuk menutup</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiwayatPesanan;
